/**
 * @fileoverview Utility functions for formatting numbers in various display formats
 * @active
 *
 * This module provides formatting utilities for:
 * - Financial numbers (with currency symbols and suffixes)
 * - Non-financial numbers (with appropriate suffixes)
 * - Special case handling for null/undefined values
 *
 * @see {@link src/utils/formatDate.ts} - Date formatting utilities
 * @see {@link src/components/research/components/fundraisingRounds/components/RecentResearchReports.jsx} - Component using formatNumber
 * @see {@link src/components/research/components/fundraisingRounds/components/FundraisingRoundsElement.jsx} - Component using formatNumber
 */

/**
 * Formats a non-financial number with appropriate suffixes (k, M, B, T).
 * For numbers less than 1000, it returns the number as a string.
 * For numbers 1000 and above, it adds the appropriate suffix.
 *
 * @param {number | string} num - The number to format.
 * @returns {string} The formatted number with appropriate suffix.
 *
 * @see {@link src/utils/formatDate.ts} - Date formatting utilities for converting between database and display formats
 */
export const formatNonFinancialNumber = (num: number | string): string => {
  // First convert to number to ensure consistent handling
  const parsedNum = typeof num === "string" ? parseFloat(num) : num;

  if (!isNaN(parsedNum)) {
    // Round to integer to avoid decimal places
    const roundedNum = Math.round(parsedNum);

    if (roundedNum >= 1_000_000_000_000) {
      return `${(roundedNum / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "")}T`;
    } else if (roundedNum >= 1_000_000_000) {
      return `${(roundedNum / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
    } else if (roundedNum >= 1_000_000) {
      return `${(roundedNum / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    } else if (roundedNum >= 1_000) {
      return `${(roundedNum / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
    } else {
      // For numbers less than 1000, return as integer
      return roundedNum.toString();
    }
  } else {
    return "—";
  }
};

/**
 * Formats a financial number with currency symbol and appropriate suffixes (k, M, B, T).
 * Safely handles null, undefined, and invalid string representations.
 *
 * @param {number | string | null | undefined} num - The number to format.
 * @returns {string} The formatted number with currency symbol and appropriate suffix, or empty string if invalid.
 *
 * @see {@link src/components/research/components/fundraisingRounds/components/RecentResearchReports.jsx} - Usage example
 */
export const formatNumber = (
  num: number | string | null | undefined
): string => {
  // Return empty string for null or undefined
  if (num === null || num === undefined) {
    return "";
  }

  // Handle string representation of null/undefined
  const strValue = String(num).toLowerCase();
  if (
    strValue === "$null" ||
    strValue === "null" ||
    strValue === "$undefined" ||
    strValue === "undefined" ||
    strValue === "$" || // Handle case where only $ is present
    strValue === ""
  ) {
    return "";
  }

  // Handle string with $ prefix but non-numeric content
  if (typeof num === "string" && num.startsWith("$")) {
    const numericPart = num.substring(1);
    // If not a valid number after removing $, return empty string
    if (isNaN(parseFloat(numericPart))) {
      return "";
    }
    // Otherwise, parse the numeric part
    num = parseFloat(numericPart);
  }

  const parsedNum = typeof num === "string" ? parseFloat(num) : num;

  if (!isNaN(parsedNum)) {
    if (parsedNum >= 1000000000000) {
      return `$${(parsedNum / 1000000000000).toFixed(1).replace(/\.0$/, "")}T`;
    } else if (parsedNum >= 1000000000) {
      return `$${(parsedNum / 1000000000).toFixed(1).replace(/\.0$/, "")}B`;
    } else if (parsedNum >= 1000000) {
      return `$${(parsedNum / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    } else if (parsedNum >= 1000) {
      return `$${(parsedNum / 1000).toFixed(1).replace(/\.0$/, "")}k`;
    } else {
      return `$${parsedNum}`;
    }
  } else {
    return ""; // Return empty string instead of "—" to keep consistent with the UI design
  }
};

/**
 * Formats a financial number with currency symbol and full text suffixes.
 *
 * @param {number | string} num - The number to format.
 * @returns {string} The formatted number with currency symbol and full text suffix.
 */
export const formatNumberFull = (num: number | string): string => {
  const parsedNum = typeof num === "string" ? parseFloat(num) : num;

  if (!isNaN(parsedNum)) {
    if (parsedNum >= 1000000000000) {
      return `$${(parsedNum / 1000000000000).toFixed(1).replace(/\.0$/, "")} Trillion(s)`;
    } else if (parsedNum >= 1000000000) {
      return `$${(parsedNum / 1000000000).toFixed(1).replace(/\.0$/, "")} Billion(s)`;
    } else if (parsedNum >= 1000000) {
      return `$${(parsedNum / 1000000).toFixed(1).replace(/\.0$/, "")} Million(s)`;
    } else if (parsedNum >= 1000) {
      return `$${(parsedNum / 1000).toFixed(1).replace(/\.0$/, "")} thousand(s)`;
    } else {
      return `$${parsedNum}`;
    }
  } else {
    return "—";
  }
};

/**
 * Formats a number with comma separators for thousands.
 * Used for displaying exact numbers (like employee counts) with proper formatting.
 *
 * @param {number | string | null | undefined} num - The number to format.
 * @returns {string | null} The formatted number with commas, or null if invalid.
 *
 * @see {@link formatNonFinancialNumber} - For formatting numbers with suffixes
 */
export const formatMoney = (
  num: number | string | null | undefined
): string | null => {
  // Handle null/undefined values and their string representations
  if (num === null || num === undefined) return null;

  // Handle string representation of null/undefined
  const strValue = String(num).toLowerCase();
  if (
    strValue === "$null" ||
    strValue === "null" ||
    strValue === "$undefined" ||
    strValue === "undefined" ||
    strValue === "$" ||
    strValue === ""
  ) {
    return null;
  }

  // Handle string with $ prefix but non-numeric content
  if (typeof num === "string" && num.startsWith("$")) {
    const numericPart = num.substring(1);
    // If not a valid number after removing $, return null
    if (isNaN(parseFloat(numericPart))) {
      return null;
    }
    // Otherwise, parse the numeric part
    num = parseFloat(numericPart);
  }

  // Handle both string and number inputs
  const parsedNum = typeof num === "string" ? parseFloat(num) : num;

  // Return null for invalid numbers
  if (isNaN(parsedNum)) return null;

  // Round to integer to avoid decimal places for employee counts
  const roundedNum = Math.round(parsedNum);

  // Format with commas for thousands
  return roundedNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
